import type { AdsWindow, AdsPendingAction, AdsAgentAlert } from '../../hooks/useSellerAdsAgent';

export const CLASSIFICATION_STYLES: Record<string, { label: string; badge: string; dot: string }> = {
  critical: { label: 'Critica', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500' },
  attention: { label: 'Atencao', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  opportunity: { label: 'Escalar', badge: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  learning: { label: 'Aprendizado', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  healthy: { label: 'Saudavel', badge: 'bg-green-50 text-green-700', dot: 'bg-green-400' },
  paused: { label: 'Pausada', badge: 'bg-gray-100 text-gray-500', dot: 'bg-gray-400' },
  no_data: { label: 'Sem dados', badge: 'bg-gray-100 text-gray-500', dot: 'bg-gray-300' },
};

export const SEVERITY_STYLES: Record<string, string> = {
  critica: 'border-red-300 bg-red-50',
  alta: 'border-amber-300 bg-amber-50',
  media: 'border-blue-200 bg-blue-50',
  baixa: 'border-gray-200 bg-gray-50',
};

export const MARKETPLACE_LABELS: Record<string, string> = {
  shopee: 'Shopee',
  mercadolivre: 'Mercado Livre',
};

export const ACTION_TYPE_LABELS: Record<string, string> = {
  scale_budget: 'Escalar orcamento',
  budget_edit: 'Ajustar orcamento',
  roas_target_edit: 'Ajustar meta ROAS/ACOS',
  pause_campaign: 'Pausar campanha',
  resume_campaign: 'Reativar campanha',
};

export function fmtMoney(v: number | null | undefined): string {
  return `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function describeParams(a: AdsPendingAction): string {
  const p = a.params || {};
  if (p.budget != null) return `Novo orcamento: ${fmtMoney(p.budget)}`;
  if (p.roasTarget != null) return `Nova meta ROAS: ${p.roasTarget}x`;
  if (p.acosTarget != null) return `Novo ACOS objetivo: ${p.acosTarget}%`;
  if (p.status === 'paused') return 'A campanha sera pausada';
  if (p.status === 'active') return 'A campanha sera reativada';
  return '';
}

export function MetricPill({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-md bg-gray-50 px-2 py-1.5 text-center" title={hint}>
      <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
      <div className="text-xs font-semibold text-gray-800">{value}</div>
    </div>
  );
}

export function WindowRow({ title, w }: { title: string; w: AdsWindow }) {
  return (
    <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-8">
      <div className="col-span-4 flex items-center text-[11px] font-semibold text-gray-500 sm:col-span-1">{title}</div>
      <MetricPill label="Impr." value={String(w.impressions)} />
      <MetricPill label="Cliques" value={`${w.clicks} (${w.ctr}%)`} hint="Cliques (CTR)" />
      <MetricPill label="Gasto" value={fmtMoney(w.spend)} />
      <MetricPill label="Pedidos" value={`${w.directOrders}${w.broadOrders ? ` +${w.broadOrders}i` : ''}`} hint="Diretos + indiretos/broad" />
      <MetricPill label="Receita" value={fmtMoney(w.totalRevenue)} />
      <MetricPill label="ROAS" value={`${w.roas}x`} />
      <MetricPill label="ACOS" value={w.acos >= 999 ? '—' : `${w.acos}%`} />
    </div>
  );
}

export function AlertCard({ alert, onUpdate }: { alert: AdsAgentAlert; onUpdate: (id: string, status: 'resolved' | 'dismissed') => void }) {
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${SEVERITY_STYLES[alert.severity] || SEVERITY_STYLES.media}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-800">{alert.title}</span>
            <span className="rounded bg-white/70 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">{MARKETPLACE_LABELS[alert.marketplace] || alert.marketplace}</span>
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-gray-600">{alert.message}</p>
        </div>
        <div className="flex shrink-0 gap-1">
          <button onClick={() => onUpdate(alert.id, 'resolved')} className="rounded bg-white px-2 py-1 text-[10px] font-medium text-green-700 shadow-sm hover:bg-green-50" title="Marcar como resolvido">Resolvido</button>
          <button onClick={() => onUpdate(alert.id, 'dismissed')} className="rounded bg-white px-2 py-1 text-[10px] font-medium text-gray-500 shadow-sm hover:bg-gray-50" title="Descartar">Ignorar</button>
        </div>
      </div>
    </div>
  );
}

export function PendingActionRow({ action, onExecute, onDismiss, busy }: { action: AdsPendingAction; onExecute: (a: AdsPendingAction) => void; onDismiss: (a: AdsPendingAction) => void; busy: boolean }) {
  return (
    <div className="rounded-md border border-emerald-200 bg-white px-2.5 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-700">{ACTION_TYPE_LABELS[action.tipo] || action.tipo}</span>
            <span className="text-[11px] font-semibold text-gray-800">{action.acao}</span>
          </div>
          <p className="mt-0.5 text-[10px] font-medium text-emerald-700">{describeParams(action)}</p>
          {action.justificativa && <p className="text-[10px] text-gray-500">{action.justificativa}</p>}
        </div>
        <div className="flex shrink-0 gap-1.5">
          <button onClick={() => onExecute(action)} disabled={busy} className="rounded-md bg-emerald-600 px-2.5 py-1.5 text-[11px] font-bold text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? 'Executando...' : 'Executar'}
          </button>
          <button onClick={() => onDismiss(action)} disabled={busy} className="rounded-md border border-gray-200 px-2 py-1.5 text-[11px] text-gray-500 hover:bg-gray-50 disabled:opacity-50">
            Descartar
          </button>
        </div>
      </div>
    </div>
  );
}
